import type { PathGraph, PathEdge, PathResult, PathStep, EvalContext } from '../types';
import { evaluate } from '../expression/evaluator';

/**
 * BFS pathfinder on the zone graph.
 *
 * Finds the shortest paths from one zone to another,
 * filtering by what is currently accessible given age and items.
 */
export function findPaths(
  graph: PathGraph,
  from: string,
  to: string,
  ctx: EvalContext,
  maxPaths = 5,
): PathResult[] {
  if (from === to) {
    return [{
      steps: [{ zone: from, edge: null }],
      childAccessible: true,
      adultAccessible: true,
    }];
  }

  const results: PathResult[] = [];

  // BFS with path tracking
  interface BfsState {
    zone: string;
    path: PathStep[];
    visitedZones: Set<string>;
  }

  const queue: BfsState[] = [{
    zone: from,
    path: [{ zone: from, edge: null }],
    visitedZones: new Set([from]),
  }];

  // Limit search to prevent explosion
  let iterations = 0;
  const MAX_ITERATIONS = 10_000;

  while (queue.length > 0 && results.length < maxPaths && iterations < MAX_ITERATIONS) {
    iterations++;
    const state = queue.shift()!;

    const outEdges = graph.adjacency.get(state.zone) ?? [];

    // Deduplicate edges to same destination (keep best per toZone)
    const edgesByDest = new Map<string, PathEdge[]>();
    for (const edge of outEdges) {
      if (state.visitedZones.has(edge.toZone)) continue;
      if (!edgesByDest.has(edge.toZone)) edgesByDest.set(edge.toZone, []);
      edgesByDest.get(edge.toZone)!.push(edge);
    }

    for (const [destZone, edges] of edgesByDest) {
      // Try each edge to this destination, find one that works
      let usableEdge: PathEdge | null = null;
      for (const edge of edges) {
        if (!edge.requirement || evaluate(edge.requirement, ctx)) {
          usableEdge = edge;
          break;
        }
      }

      if (!usableEdge) continue;

      const newPath: PathStep[] = [...state.path, { zone: destZone, edge: usableEdge }];

      if (destZone === to) {
        // Evaluate accessibility for both ages
        const childResult = evaluatePathForAge(newPath, {
          ...ctx,
          age: 'child',
        });
        const adultResult = evaluatePathForAge(newPath, {
          ...ctx,
          age: 'adult',
        });

        results.push({
          steps: newPath,
          childAccessible: childResult,
          adultAccessible: adultResult,
        });
      } else {
        const newVisited = new Set(state.visitedZones);
        newVisited.add(destZone);
        queue.push({
          zone: destZone,
          path: newPath,
          visitedZones: newVisited,
        });
      }
    }
  }

  return results;
}

/**
 * Check if an entire path is traversable at a given age.
 */
function evaluatePathForAge(steps: PathStep[], ctx: EvalContext): boolean {
  for (const step of steps) {
    if (step.edge?.requirement) {
      if (!evaluate(step.edge.requirement, ctx)) return false;
    }
  }
  return true;
}

/**
 * Find paths considering both ages independently.
 * Returns paths that are accessible by at least one age.
 */
export function findPathsBothAges(
  graph: PathGraph,
  from: string,
  to: string,
  items: Record<string, number>,
  settings: Record<string, unknown>,
  helpers: Record<string, string>,
  maxPaths = 5,
): PathResult[] {
  // Find paths accessible as child
  const childCtx: EvalContext = { age: 'child', items, settings, helpers };
  const childPaths = findPaths(graph, from, to, childCtx, maxPaths);

  // Find paths accessible as adult
  const adultCtx: EvalContext = { age: 'adult', items, settings, helpers };
  const adultPaths = findPaths(graph, from, to, adultCtx, maxPaths);

  // Merge and deduplicate by path zone sequence
  const seen = new Set<string>();
  const merged: PathResult[] = [];

  for (const path of [...childPaths, ...adultPaths]) {
    const key = path.steps.map(s => s.zone).join('->');
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(path);
  }

  // Sort by path length (shortest first)
  merged.sort((a, b) => a.steps.length - b.steps.length);

  return merged.slice(0, maxPaths);
}
